import { useCallback, useContext, useMemo, useRef, useSyncExternalStore } from 'react';
import { UNSAFE_NavigationContext, useLocation, useNavigate } from 'react-router-dom';
import { withoutLeaveGuard } from '@/hooks/useUnsavedChanges';

/*
  useUrlState(schema, options) — keep a page's view state (filters, search, sort, tab, view, page) in
  the URL query, so a reload, a copied link and browser Back all bring the same view back.

    const [filters, setFilters, { reset, isDefault }] = useUrlState({
      q: '',                 // string   → ?q=nobu
      system: [],            // string[] → ?system=BMS&system=LCS (repeated keys; values may contain commas)
      sort: 'attention',     // string with a default → omitted from the URL while it is the default
      page: 1,               // number   → ?page=3
      grouped: false,        // boolean  → ?grouped=1
    });
    setFilters({ system: ['BMS'], page: 1 });          // merge a patch
    setFilters((prev) => ({ page: prev.page + 1 }));   // or an updater
    reset();                                            // back to the defaults (keeps other params)

  - Writes use history REPLACE (one entry per page, not one per keystroke), so Back leaves the page
    instead of stepping through every filter change. Pass { replace: false } to a single call to push.
  - Values equal to their default are left out of the URL; params that are not in the schema (?tab=,
    ?stage=, another hook's keys) are kept untouched, so several hooks can share one URL.
  - Several writes in the same event handler merge (each reads the latest URL, not the last render).
  - The returned state changes at once (in the same event), so it can drive a controlled input:
    <input value={filters.q} onChange={(e) => setFilters({ q: e.target.value })} />. React Router 7
    applies URL changes inside a transition, and a controlled input fed from a transition loses
    keystrokes on a slow page; this hook reads its own writes back right away instead. To keep typing
    smooth on a big list, filter with useDeferredValue(filters.q).
  - An array whose default is not empty can still be cleared: [] is written as `?status=`.
  - These writes never trigger the unsaved-changes "Leave without saving?" prompt (they do not leave
    the page). Use useTabParam's `guard` when a switch unmounts dirty content.
  - Custom types: { defaultValue, parse(raw: string | null), serialize(value) → string | null }.
  - options.prefix namespaces the keys (two tables on one page: { prefix: 'lpo.' } → ?lpo.q=…).

  useUrlParam(key, defaultValue, options) → [value, setValue] for a single param.
  useTabParam(tabs, options) → [tab, setTab] for ?tab= (see below).
*/

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off']);

function isCodec(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value) && typeof value.parse === 'function';
}

function defaultOf(spec) {
  return isCodec(spec) ? spec.defaultValue : spec;
}

function sameValue(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => String(item) === String(b[index]));
  }
  return a === b;
}

function readParam(params, key, spec) {
  if (isCodec(spec)) {
    const raw = params.has(key) ? params.get(key) : null;
    if (raw === null) return spec.defaultValue;
    const parsed = spec.parse(raw);
    return parsed === undefined ? spec.defaultValue : parsed;
  }
  if (Array.isArray(spec)) {
    // A key that is present wins over the default: `?status=` is "nothing picked", not "the default".
    if (!params.has(key)) return spec;
    return params.getAll(key).filter((value) => value !== '');
  }
  if (!params.has(key)) return spec;
  const raw = params.get(key);
  if (typeof spec === 'number') {
    const number = Number(raw);
    return raw !== '' && Number.isFinite(number) ? number : spec;
  }
  if (typeof spec === 'boolean') {
    const lowered = String(raw).toLowerCase();
    if (TRUE_VALUES.has(lowered)) return true;
    if (FALSE_VALUES.has(lowered)) return false;
    return spec;
  }
  return raw;
}

// Single values are replaced in place (set keeps the param's position, so links stay readable).
function writeParam(params, key, spec, value) {
  if (value === undefined || value === null || sameValue(value, defaultOf(spec))) {
    params.delete(key);
    return;
  }
  if (isCodec(spec)) {
    const raw = spec.serialize ? spec.serialize(value) : String(value);
    if (raw === null || raw === undefined) params.delete(key);
    else params.set(key, raw);
    return;
  }
  if (Array.isArray(spec)) {
    const items = (Array.isArray(value) ? value : [value]).filter((item) => item !== null && item !== undefined && item !== '');
    if (!items.length) {
      // Empty while the default is not (a "Status" filter that defaults to Active, cleared to show all):
      // keep the key as `?status=`, otherwise the missing key would read back as the default.
      if (spec.length) params.set(key, '');
      else params.delete(key);
      return;
    }
    params.delete(key);
    items.forEach((item) => params.append(key, String(item)));
    return;
  }
  params.set(key, typeof spec === 'boolean' ? (value ? '1' : '0') : String(value));
}

/** Parse a query string with a schema — exported for tests and for pages that build links. */
export function parseUrlState(search, schema, prefix = '') {
  const params = new URLSearchParams(search || '');
  const state = {};
  Object.keys(schema).forEach((key) => {
    state[key] = readParam(params, prefix + key, schema[key]);
  });
  return state;
}

/** Write `state` into a query string (other params kept). Returns the string without '?'. */
export function writeUrlState(search, schema, state, prefix = '') {
  const params = new URLSearchParams(search || '');
  Object.keys(schema).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(state, key)) writeParam(params, prefix + key, schema[key], state[key]);
  });
  return params.toString();
}

function schemaSignature(schema) {
  return JSON.stringify(Object.keys(schema).map((key) => [key, defaultOf(schema[key])]));
}

/*
  The last URL written through these hooks, per router (history object). React Router 7 renders a new
  location inside React.startTransition, so the rendered `location` lags behind the URL for as long as a
  slow page takes to render — and a controlled input fed from a transition gets its old value put back
  after each keystroke (lost characters, a jumping caret). Every hook on the page subscribes to this
  store with useSyncExternalStore, whose updates are synchronous, and reads the written URL until the
  router catches up. It is only trusted while it is still the live URL, so Back, a <Link> or any other
  navigation simply wins.
*/
const writeStores = new WeakMap();
const NO_STORE = { get: () => null, set: () => {}, subscribe: () => () => {} };

function writeStoreFor(navigator) {
  if (!navigator || typeof navigator !== 'object') return NO_STORE;
  let store = writeStores.get(navigator);
  if (!store) {
    let value = null;
    const listeners = new Set();
    store = {
      get: () => value,
      set: (next) => {
        value = next;
        listeners.forEach((listener) => listener());
      },
      subscribe: (listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
    };
    writeStores.set(navigator, store);
  }
  return store;
}

function liveLocationOf(navigator) {
  const live = navigator && navigator.location;
  return live && typeof live.search === 'string' && typeof live.pathname === 'string' ? live : null;
}

export function useUrlState(schema, options = {}) {
  const { prefix = '' } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const signature = schemaSignature(schema);

  const store = writeStoreFor(navigator);
  const written = useSyncExternalStore(store.subscribe, store.get, store.get);
  const live = liveLocationOf(navigator);
  const renderedSearch = location.search || '';
  // Our own write, while the router is still rendering towards it (see writeStoreFor).
  const search = written && live
    && written.pathname === live.pathname
    && written.search === (live.search || '')
    ? written.search
    : renderedSearch;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const state = useMemo(() => parseUrlState(search, schemaRef.current, prefix), [search, prefix, signature]);

  // The URL as it is right now — the history object updates synchronously on navigate, so several
  // writes in one handler see each other (the rendered `location` would still be the old one).
  const currentLocation = useCallback(() => liveLocationOf(navigator) || location, [navigator, location]);

  const setState = useCallback((patchOrUpdater, callOptions = {}) => {
    const { replace = true, reset = false } = callOptions;
    const current = currentLocation();
    const activeSchema = schemaRef.current;
    const previous = parseUrlState(current.search, activeSchema, prefix);
    const patch = typeof patchOrUpdater === 'function' ? patchOrUpdater(previous) : patchOrUpdater;
    const base = reset
      ? Object.fromEntries(Object.keys(activeSchema).map((key) => [key, defaultOf(activeSchema[key])]))
      : previous;
    const next = { ...base, ...(patch || {}) };
    const search = writeUrlState(current.search, activeSchema, next, prefix);
    const nextSearch = search ? `?${search}` : '';
    const currentSearch = current.search || '';
    if (nextSearch === currentSearch || (nextSearch === '' && currentSearch === '?')) return;
    withoutLeaveGuard(() => navigate(
      { pathname: current.pathname, search: nextSearch, hash: current.hash || '' },
      { replace, state: replace ? current.state : undefined },
    ));
    // Show the new values now (a synchronous update), not when the router's transition commits.
    const after = liveLocationOf(navigator);
    if (after) store.set({ pathname: after.pathname, search: after.search || '' });
  }, [currentLocation, navigate, navigator, prefix, store]);

  const reset = useCallback((callOptions) => setState({}, { ...callOptions, reset: true }), [setState]);

  const isDefault = useMemo(
    () => Object.keys(schemaRef.current).every((key) => sameValue(state[key], defaultOf(schemaRef.current[key]))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, signature],
  );

  return [state, setState, { reset, isDefault }];
}

/** One URL param: const [q, setQ] = useUrlParam('q', ''). */
export function useUrlParam(key, defaultValue = '', options) {
  const [state, setState] = useUrlState({ [key]: defaultValue }, options);
  const setValue = useCallback((next, callOptions) => setState(
    (previous) => ({ [key]: typeof next === 'function' ? next(previous[key]) : next }),
    callOptions,
  ), [key, setState]);
  return [state[key], setValue];
}

/*
  useTabParam(tabs, options) — the open tab lives in ?tab= (replace), validated against `tabs`.

    const { confirmLeave } = useUnsavedChanges(dirty);
    const [tab, setTab] = useTabParam(['stages', 'material', 'files'], { guard: () => confirmLeave() });
    <Tabs value={tab} onValueChange={setTab}>…</Tabs>      // or <TabsBar value={tab} onValueChange={setTab} …/>

  tabs: string[] or [{ value }] (only the tabs this user can see — an unknown or hidden ?tab= falls back
  to `defaultTab`, the first tab by default). setTab(next) resolves true when the tab changed.
  Options: param ('tab'), defaultTab, guard(next, current) → boolean | Promise<boolean> (ask before
  leaving dirty content), keepDefaultInUrl (write ?tab= even for the default tab; default false).
*/
export function useTabParam(tabs, options = {}) {
  const { param = 'tab', guard, keepDefaultInUrl = false } = options;
  const values = (tabs || []).map((tab) => (tab && typeof tab === 'object' ? tab.value : tab)).filter(Boolean);
  const defaultTab = options.defaultTab && values.includes(options.defaultTab) ? options.defaultTab : values[0];
  const [raw, setRaw] = useUrlParam(param, '');
  const tab = values.includes(raw) ? raw : defaultTab;
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const guardRef = useRef(guard);
  guardRef.current = guard;
  const valuesKey = values.join('|');

  const setTab = useCallback(async (next) => {
    const allowed = valuesKey.split('|');
    if (!allowed.includes(next) || next === tabRef.current) return false;
    if (guardRef.current) {
      const ok = await guardRef.current(next, tabRef.current);
      if (!ok) return false;
    }
    setRaw(next === defaultTab && !keepDefaultInUrl ? '' : next);
    return true;
  }, [valuesKey, defaultTab, keepDefaultInUrl, setRaw]);

  return [tab, setTab];
}

export default useUrlState;
